import { useState, type ImgHTMLAttributes, type ReactNode } from 'react';
import { getOptimizedMspLogoUrl, MSP_LOGO_PRESETS } from '@/lib/mspLogo';
import { getInitials } from '@/lib/mspProfileDisplay';
import { cn } from '@/lib/utils';

interface MspLogoImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  /** Originele logo_url uit de database. */
  logoUrl: string | null | undefined;
  /** Weergavecontext: bepaalt de gevraagde intrinsieke grootte. */
  variant?: keyof typeof MSP_LOGO_PRESETS;
  /** Wordt gerenderd als er geen logo is of als ook het origineel faalt. */
  fallback?: ReactNode;
  /** Bedrijfsnaam: zonder expliciete fallback verschijnt een initialen-avatar. */
  name?: string | null;
  /** Extra klassen (bv. tekstgrootte) voor de initialen-avatar. */
  avatarClassName?: string;
}

/**
 * MSP-logo met geoptimaliseerde transform-URL en eenmalige onError-fallback
 * naar de originele logo_url. Faalt ook het origineel, dan volgt de placeholder.
 * Er wordt nooit opnieuw geprobeerd (geen retry-loop).
 */
export function MspLogoImage({ logoUrl, variant = 'card', fallback, name, avatarClassName, ...imgProps }: MspLogoImageProps) {
  const [stage, setStage] = useState<'optimized' | 'original' | 'failed'>('optimized');

  if (!logoUrl || stage === 'failed') {
    if (fallback !== undefined) return <>{fallback}</>;
    return (
      <span
        aria-hidden="true"
        className={cn(
          'flex h-full w-full items-center justify-center bg-primary/10 font-semibold tracking-wide text-primary',
          avatarClassName,
        )}
      >
        {getInitials(name)}
      </span>
    );
  }

  const optimized = getOptimizedMspLogoUrl(logoUrl, MSP_LOGO_PRESETS[variant]) ?? logoUrl;
  const src = stage === 'optimized' ? optimized : logoUrl;

  return (
    <img
      {...imgProps}
      src={src}
      onError={() => {
        setStage((prev) => {
          if (prev === 'optimized' && optimized !== logoUrl) return 'original';
          return 'failed';
        });
      }}
    />
  );
}
